import { FC, useRef, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import Swal from "sweetalert2";
import { Product, ProductCurrencyPrice } from "../../interfaces/Product";
import { updateProduct } from "../../api/ProductsCRUD";
import useAccessToken from "../../hooks/AccessToken";
import { SketchPicker } from "react-color";
import { KTSVG } from "../../_metronic/helpers";
import { AddLabel } from "../Labels/AddLabel";
import { Modal } from "bootstrap";
import { SelectOption } from "../../interfaces/SelectOption";
import useSWR, { mutate } from "swr";
import { Label } from "../../interfaces/Label";
import { currencyFormatter, customNumberFormatString, fetcherSWR, formatDateWithDayName, getDateOnly, handleCaps, isValidDateFormat } from "../../functions/general";
import { useIntl } from "react-intl";
import { getLabelProduct } from "../../api/LabelsCRUD";
import { Unit } from "../../interfaces/Unit";
import { getUnit } from "../../api/UnitsCRUD";
import { AddUnit } from "../Units/AddUnit";
import useHandleCloseModal from "../../hooks/HandleCloseModal";
import VirtualizedSelect, { VirtualizedSelectOption } from "../Select/SelectVirtualized";
import useUser from "../../hooks/User";
import { usePipelineStore } from "../../stores/PipelineStore";
import { useSettingsStore } from "../../stores/SettingsStore";
import { SettingMultiPipelines } from "../../interfaces/Settings";
import { MasterCurrency } from "../../interfaces/Currency";
import { useCurrencyStore } from "../../stores/CurrencyStore";
import { DatePicker, Popover, Toggle, Whisper } from "rsuite";
import { API_URL } from "../../api/SettingCRUD";
import InfoIcon from '@mui/icons-material/Info';
import { useCustomeStore } from "../../stores/CustomeStore";
import { checkIsCompanyHarriette } from "../../functions/customCase";
import useTodayRange from "../../hooks/TodayRange";
import moment from "moment";
import { IPriceVariant } from "../../interfaces/PriceVariant";
import useModalEffect from "../../hooks/useModalEffect";

interface Props {
    product: Product
}

interface UnitOption {
    id: string
    name: string
    qty: number

}

const UpdateProduct: FC<Props> = ({ product }) => {
    const { multiPipelines } = usePipelineStore()
    const { companyId } = useCustomeStore()
    const [templatePipelineOptions, setTemplatePipelineOptions] = useState<SelectOption[]>([])
    const [currencyOptions, setCurrencyOptions] = useState<VirtualizedSelectOption[]>([])
    const [labelOptions, setLabelOptions] = useState<SelectOption[]>([])
    const [isLoadingLabel, setIsLoadingLabel] = useState(false)
    const [priceProduct, setPriceProduct] = useState('');
    const [selectedLabels, setSelectedLabels] = useState(product.productLabels.map(data => {
        return {
            label: data.label.name,
            value: data.label.id
        }
    }))
    const [showAddLabelModal, setShowAddLabelModal] = useState(false)
    const [unitOptions, setUnitOptions] = useState<VirtualizedSelectOption[]>([])
    const [isLoadingUnit, setIsLoadingUnit] = useState(false)
    const [selectedUnits, setSelectedUnits] = useState<UnitOption[]>(product.productUnits.map(data => {
        return {
            name: data.unit.name,
            id: data.unit.id,
            qty: data.convertionToPieces
        }
    }))
    const [showAddUnitModal, setShowAddUnitModal] = useState(false)
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const [modalBtnLoading, setModalBtnLoading] = useState(false)
    const { multiCurrencies } = useCurrencyStore()
    const token = useAccessToken()
    const intl = useIntl()
    const user = useUser()
    const { settings } = useSettingsStore()
    const allOption = [{ label: intl.formatMessage({ id: "FORM.LABEL.ALL" }, { title: intl.formatMessage({ id: "FORM.LABEL.TEMPLATE" }, { title: settings.pipeline_title }) }), value: 'all' }]
    const checkIsHarriette = checkIsCompanyHarriette(companyId, user)
    const todayRange = useTodayRange();
    const [dateProduct, setDateProduct] = useState<string | null>(null)
    const [selectedVariantPrice, setSelectedVariantPrice] = useState<IPriceVariant>();

    const formik = useFormik({
        initialValues: {
            name: product.name,
            // currency: '',
            label: product.productLabels,
            color: product.color ?? "#D33115",
            sku: product.sku,
            note: product.note,
            // price: product.price,
            prices: product.prices?.map(price => ({
                currencyId: price.currencyId.toString(),
                price: currencyFormatter(price.price?.toString() || '', price.currencyIsRounded),
                variantPriceId: price.variantPriceId,
            })) || [],
            // vatPercentage: product.vatPercentage,
            isUsingVat: product.isUsingVat,
            isCanAccess: product.isCanAccess,
            accessTemplatePipeline: multiPipelines.length === product.listLeadTemplateAccess.length ? allOption : multiPipelines.filter(data => product.listLeadTemplateAccess.includes(data.id)).map(data => { return { label: data.name, value: data.id } })
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })),
            // currency: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "MASTER.CURRENCIES.CURRENCY" }) })),
            color: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.COLOR" }) })),
            isUsingVat: Yup.boolean().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.SHOW_VAT" }) })),
            isCanAccess: Yup.boolean().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PRODUCT_STATUS" }) })),
        }),
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            // convert the labels to id and name
            const labels = selectedLabels.map((data: VirtualizedSelectOption) => {
                return {
                    labelId: data.value,
                    labelName: data.label
                }
            })

            let accessTemplatePipeline = values.accessTemplatePipeline.map(item => item.value);
            if (values.accessTemplatePipeline.find(product => product.value === 'all')) accessTemplatePipeline = templatePipelineOptions.filter(item => item.value !== 'all').map(item => item.value);

            const checkQtyUnit = selectedUnits.some(unit => unit.qty === 0);
            if (checkQtyUnit) {
                Swal.fire({
                    icon: 'error',
                    title: intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.QUANTITY_UNIT" }) }),
                    confirmButtonText: 'Ok'
                })
                return
            }

            const units = selectedUnits.map((data: UnitOption) => {
                return {
                    unitId: data.id,
                    unitName: data.name,
                    convertionToPieces: data.qty
                }
            })

            setSubmitting(true);
            setModalBtnLoading(true)

            try {
                const currencyPrices: ProductCurrencyPrice[] =
                    values.prices.map((data) => {
                        const currency = multiCurrencies.find(c => c.id === Number(data.currencyId));
                        return {
                            currencyId: Number(data.currencyId),
                            price: data.price ? Number(data.price.replaceAll(",", "")) : null, // Ensure price is always a number
                            currencyCode: currency?.code || '',
                            currencyName: currency?.name || '',
                            currencyIsRounded: currency?.isRounded || false,
                            variantPriceId: data?.variantPriceId || null
                        }
                    })

                const newValues = {
                    ...values,
                    note: checkIsHarriette && dateProduct !== null && dateProduct !== '' ? formatDateWithDayName(dateProduct || "") : checkIsHarriette && (dateProduct === null || dateProduct === '') ? '-' : formik.values.note
                }
                const response = await updateProduct(product.id, newValues.name, newValues.color, accessTemplatePipeline, token, labels, units, newValues.sku, newValues.note, currencyPrices, newValues.isUsingVat, newValues.isCanAccess)
                if (response.data.success) {
                    Swal.fire({
                        icon: 'success',
                        heightAuto: false,
                        title: intl.formatMessage({ id: 'FORM.VALIDATION.UPDATE_SUCCESS' }),
                        timer: 2000,
                        timerProgressBar: true
                    })
                    closeModalRef.current?.click();
                    // setTablesData('products', (tablesData.products.map(item => {
                    //     if (item.id === product.id) {
                    //         return {
                    //             ...item,
                    //             name: response.data.data.name,
                    //             color: response.data.data.color,
                    //             productLabels: response.data.data.productLabels,
                    //             productUnits: response.data.data.productUnits,
                    //             sku: response.data.data.sku,
                    //             note: response.data.data.note,
                    //             // price: response.data.data.price,
                    //             prices: response.data.data.prices,
                    //             isUsingVat: response.data.data.isUsingVat,
                    //             listLeadTemplateAccess: response.data.data.listLeadTemplateAccess
                    //         }
                    //     } else {
                    //         return item
                    //     }
                    // })));
                    mutate(`${API_URL}products`)
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: error.response.data.message,
                    confirmButtonText: 'Ok',
                    heightAuto: false,
                })
            } finally {
                setModalBtnLoading(false)
                setDateProduct(null)
            }
        }
    });

    useEffect(() => {
        let priceValue: string = '';
        if (product.price && product.price > 0) {
            priceValue = currencyFormatter(product.price.toString(), user?.data?.companyCurrencyNeedRound)
        } else if (product.price === 0) {
            priceValue = '0'
        }
        setPriceProduct(priceValue);
    }, [product]);

    useEffect(() => {
        // Get the modal element
        const modalElement = modalRef.current;

        // Define the event listener
        const handleModalHidden = () => {
            formik.resetForm()
            setSelectedUnits([])
        };

        // Attach the event listener
        modalElement?.addEventListener('hidden.bs.modal', handleModalHidden);

        // Cleanup
        return () => {
            modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
    }, [formik]);

    const getDataLabel = async () => {
        setIsLoadingLabel(true)
        try {
            const response = await getLabelProduct(token)
            if (response.data.success) {
                setLabelOptions(response.data.data.map((label: Label) => {
                    return {
                        value: label.id,
                        label: label.name
                    }
                }))
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: error.response.data.message,
                confirmButtonText: 'Ok'
            })
        } finally {
            setIsLoadingLabel(false)
        }
    }

    const getDataUnit = async () => {
        setIsLoadingUnit(true)
        try {
            const response = await getUnit(token)
            if (response.data.success) {
                const optionIds = product.productUnits.map(opt => opt.unit.id);
                const result = response.data.data.filter((unit: Unit) => !optionIds.includes(unit.id)).map((unit: Unit) => {
                    return {
                        value: unit.id,
                        label: unit.name
                    }
                });
                setUnitOptions(result);
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: error.response.data.message,
                confirmButtonText: 'Ok'
            })
        } finally {
            setIsLoadingUnit(false)
        }
    }

    function handleInputQtyUnit(index: number, e: React.ChangeEvent<HTMLInputElement>) {
        var value = e.target.value ?? 0
        value = customNumberFormatString(value)
        selectedUnits[index].qty = Number(value.replaceAll(",", ""))
        setSelectedUnits([...selectedUnits])
    }

    const handleDeleteUnit = (key: number, data: UnitOption) => {
        selectedUnits.splice(key, 1);
        setSelectedUnits([...selectedUnits]);

        if (unitOptions.some(opt => opt.value !== data.id)) {
            setUnitOptions([...unitOptions, {
                value: data.id,
                label: data.name
            }])
        }
    }

    useModalEffect({
        showModal: showAddLabelModal,
        setShowModal: setShowAddLabelModal,
        modalId: `add-label-modal`
    })

    useModalEffect({
        showModal: showAddUnitModal,
        setShowModal: setShowAddUnitModal,
        modalId: `add-unit-modal`
    })

    useEffect(() => {
        let data = multiPipelines.map((pipeline: SettingMultiPipelines) => {
            return {
                value: pipeline.id,
                label: pipeline.name
            }
        })

        setTemplatePipelineOptions([...allOption, ...data])
    }, [multiPipelines])

    useEffect(() => {
        let data = multiCurrencies.map((currency: MasterCurrency) => {
            return {
                value: currency.id.toString(),
                label: currency.name
            }
        })

        setCurrencyOptions([...data])
    }, [multiCurrencies])

    useEffect(() => {
        if (checkIsHarriette && isValidDateFormat(product.note || "")) {
            setDateProduct(getDateOnly(product.note || ""))
        }
    }, [product, checkIsHarriette])

    useEffect(() => {
        getDataLabel()
        getDataUnit()
    }, [])

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);
    const header = token ? { 'X-Auth-token': token } : {}

    const fetcherSWRWithTransformSubProgressOption = (
        args: [string | undefined, typeof header, any]
    ): Promise<{ value: string; label: string }[]> =>
        fetcherSWR(args).then((data: IPriceVariant[]) =>
            data.map((sp) => ({
                value: sp.id,
                label: `${sp.variantPriceName}`,
            }))
        );

    const { data: _variantPriceOption = [], error: variantPriceError, isLoading: variantPriceIsLoading } = useSWR([`${API_URL}variant-prices`, header, {}], fetcherSWRWithTransformSubProgressOption)


    const handlePriceChange = (currency: MasterCurrency, e: React.ChangeEvent<HTMLInputElement>, selectedVariantId: string | null) => {
        const value = e.target.value;
        console.log({ currency, value, selectedVariantId }, 226);
        // If the input is empty, reset the price
        if (!value || value === '') {
            setPriceProduct('');

            const updatedCurrencyPrices = formik.values.prices.map((item) =>
                item.variantPriceId === selectedVariantId && Number(item.currencyId) === currency.id
                    ? { ...item, price: '' } // Empty the price only for the selected variant and currency
                    : item
            );

            formik.setFieldValue('prices', updatedCurrencyPrices);
            return;
        }

        // Format the value if it exists
        const price = value ? currencyFormatter(value, currency.isRounded) : null;

        const updatedCurrencyPrices = formik.values.prices.map((item) =>
            item.variantPriceId === selectedVariantId && Number(item.currencyId) === currency.id
                ? { ...item, price }
                : item
        );

        // If no entry exists for the selected variant and currency, add a new one
        if (
            !updatedCurrencyPrices.some(
                (item) => item.variantPriceId === selectedVariantId && Number(item.currencyId) === currency.id
            )
        ) {
            updatedCurrencyPrices.push({
                currencyId: String(currency.id),
                price,
                variantPriceId: selectedVariantId,
            });
        }

        // Set the updated prices back to formik
        formik.setFieldValue('prices', updatedCurrencyPrices);
    };

    return (
        <>
            <div className={`modal fade ${(!showAddLabelModal && !showAddUnitModal) ? 'show' : 'hide'}`} tabIndex={-1} id={`update-product-modal-${product.id}`} ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '800px' }}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })} {product.name}</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                onClick={handleCloseModal}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <form onSubmit={formik.handleSubmit} noValidate>
                            <div className='modal-body'>
                                <div className='row'>
                                    <div className='col-12 col-md-8 px-4'>
                                        <label className='form-label fs-6 fw-bolder text-dark required'>
                                            {intl.formatMessage({ id: 'FORM.LABEL.NAME' })}
                                        </label>
                                        <div className='input-group mb-3'>
                                            <input
                                                placeholder={intl.formatMessage({ id: 'MENU.PRODUCTS' })}
                                                {...formik.getFieldProps('name')}
                                                className={clsx('form-control', {
                                                    'is-invalid': formik.touched.name && formik.errors.name,
                                                })}
                                                type='text'
                                                name='name'
                                                autoComplete='off'
                                                onInput={(e) => handleCaps(e)}
                                            />
                                        </div>
                                        {formik.touched.name && formik.errors.name && (
                                            <div className='fv-plugins-message-container text-danger'>
                                                <span role='alert' className='text-danger'>
                                                    {formik.errors.name}
                                                </span>
                                            </div>
                                        )}
                                        <label className='form-label fs-6 fw-bolder text-dark'>
                                            {intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE' }, { title: settings.pipeline_title })}
                                        </label>
                                        <div className='input-group mb-3'>
                                            <VirtualizedSelect
                                                className='w-100'
                                                placeholder={`${intl.formatMessage({
                                                    id: 'FORM.ACTION.CHOOSE',
                                                })} ${intl.formatMessage(
                                                    { id: 'FORM.LABEL.TEMPLATE' },
                                                    { title: settings.pipeline_title }
                                                )}`}
                                                options={templatePipelineOptions}
                                                value={formik.values.accessTemplatePipeline as any}
                                                onChange={(selectedOptions: VirtualizedSelectOption[]) => {
                                                    var endIndex = selectedOptions.length - 1
                                                    if (selectedOptions.length > 0 && selectedOptions[endIndex].value === 'all') {
                                                        formik.setFieldValue('accessTemplatePipeline', allOption)
                                                    } else {
                                                        formik.setFieldValue(
                                                            'accessTemplatePipeline',
                                                            selectedOptions.filter((item) => item.value !== 'all')
                                                        )
                                                    }
                                                }}
                                                onBlur={() =>
                                                    formik.values.accessTemplatePipeline.length === 0 &&
                                                    formik.setFieldTouched('accessTemplatePipeline', true)
                                                }
                                                isMulti
                                                isSort={false}
                                                closeMenuOnSelect={false}
                                                defaultComponentConfig={{
                                                    isMultiValuesMaxHeight: '200px'
                                                }}
                                            />
                                        </div>
                                        <label className='form-label fs-6 fw-bolder text-dark'>
                                            {intl.formatMessage({ id: 'FORM.LABEL.LABELS' })}
                                        </label>
                                        <div className='d-flex gap-4 mb-3'>
                                            <VirtualizedSelect
                                                className='w-100'
                                                placeholder={`${intl.formatMessage({
                                                    id: 'FORM.ACTION.CHOOSE',
                                                })} ${intl.formatMessage({ id: 'FORM.LABEL.LABELS' })}`}
                                                options={labelOptions}
                                                value={selectedLabels}
                                                onChange={(option) => {
                                                    // set the selected labels to add option in
                                                    setSelectedLabels((option || []) as SelectOption[])
                                                    formik.handleChange({
                                                        target: {
                                                            name: 'label',
                                                            value: option || '',
                                                        },
                                                    })
                                                }}
                                                isMulti
                                                isLoading={isLoadingLabel}
                                                isSort={false}
                                                closeMenuOnSelect={false}
                                            />
                                            <button
                                                type='button'
                                                className='btn btn-icon btn-secondary btn-sm'
                                                onClick={() => {
                                                    setShowAddLabelModal(true)
                                                }}
                                            >
                                                <i className='fas fa-plus'></i>
                                            </button>
                                        </div>
                                        <label className='form-label fs-6 fw-bolder text-dark'>
                                            {intl.formatMessage({ id: 'FORM.LABEL.UNITS' })}
                                        </label>
                                        <div className='d-flex gap-4 mb-3'>
                                            <VirtualizedSelect
                                                options={unitOptions}
                                                placeholder={`${intl.formatMessage({
                                                    id: 'FORM.ACTION.CHOOSE',
                                                })} ${intl.formatMessage({ id: 'FORM.LABEL.UNITS' })}`}
                                                onChange={(option) => {
                                                    setSelectedUnits([
                                                        ...selectedUnits,
                                                        { id: option?.value!, name: option?.label!, qty: 0 },
                                                    ])
                                                    setUnitOptions(
                                                        unitOptions.filter((unit: SelectOption) => unit.value !== option?.value)
                                                    )
                                                }}
                                                className='w-100'
                                                isLoading={isLoadingUnit}
                                                isClearable={false}
                                            />
                                            <button
                                                type='button'
                                                className='btn btn-icon btn-secondary btn-sm'
                                                onClick={() => {
                                                    setShowAddUnitModal(true)
                                                }}
                                            >
                                                <i className='fas fa-plus'></i>
                                            </button>
                                        </div>
                                        {selectedUnits.map((data: UnitOption, index: number) => (
                                            <div className='mb-3 d-flex'>
                                                <div className='input-group group-sm me-3'>
                                                    <input
                                                        type='text'
                                                        className='form-control'
                                                        value={
                                                            customNumberFormatString(String(data.qty)) === '0' ? '' : customNumberFormatString(String(data.qty))
                                                        }
                                                        onInput={(e) => handleCaps(e)}
                                                        onChange={(e) => handleInputQtyUnit(index, e)}
                                                    />
                                                    <span className='input-group-text' id='basic-addon2'>
                                                        {data.name}
                                                    </span>
                                                </div>
                                                <button
                                                    type='button'
                                                    className='btn btn-icon btn-light-youtube'
                                                    onClick={() => handleDeleteUnit(index, data)}
                                                >
                                                    <i className='fas fa-trash fs-4'></i>
                                                </button>
                                            </div>
                                        ))}
                                        <div className="row mt-2">
                                            <div className="col-12 col-md-5">
                                                <label className='form-label fs-6 fw-bolder text-dark'>SKU</label>
                                                <div className='input-group mb-3'>
                                                    <input
                                                        placeholder='SKU'
                                                        {...formik.getFieldProps('sku')}
                                                        className={clsx('form-control ', {
                                                            'is-invalid': formik.touched.sku && formik.errors.sku,
                                                        })}
                                                        type='text'
                                                        name='sku'
                                                        autoComplete='off'
                                                        onInput={(e) => handleCaps(e)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-6 col-md-4 mb-4">
                                                <div className="d-flex gap-2">
                                                    <label className='form-label fs-6 fw-bolder text-dark'>
                                                        {intl.formatMessage({ id: 'FORM.LABEL.SHOW_VAT' })}
                                                    </label>
                                                    <Whisper
                                                        placement="top"
                                                        trigger="hover"
                                                        controlId="control-id-hover"
                                                        speaker={
                                                            <Popover title={intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.INFORMATION' })} style={{ zIndex: 999999 }}>
                                                                <p>{intl.formatMessage({ id: 'FORM.LABEL.ACTIVE_VAT_ON_PIPELINE' }, { title: settings.pipeline_title })}</p>
                                                            </Popover>
                                                        }
                                                    >
                                                        <InfoIcon className="cursor-pointer" />
                                                    </Whisper>
                                                </div>
                                                <div className='mt-3 d-flex align-items-center'>
                                                    <Toggle
                                                        className="col-md-5 col-lg-3 col-xl-3"
                                                        {...formik.getFieldProps('isUsingVat')}
                                                        checked={formik.values.isUsingVat!}
                                                        onChange={(e) => formik.setFieldValue('isUsingVat', e)}
                                                    />
                                                </div>
                                                {formik.touched.isUsingVat && formik.errors.isUsingVat && (
                                                    <div className='text-danger'>{formik.errors.isUsingVat}</div>
                                                )}
                                            </div>
                                            <div className="col-6 col-md-3 mb-4">
                                                <div className="d-flex gap-2">
                                                    <label className='form-label fs-6 fw-bolder text-dark'>
                                                        Status
                                                    </label>
                                                    <Whisper
                                                        placement="top"
                                                        trigger="hover"
                                                        controlId="control-id-hover"
                                                        speaker={
                                                            <Popover title={intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.INFORMATION' })} style={{ zIndex: 999999 }}>
                                                                <p>{intl.formatMessage({ id: 'FORM.LABEL.ACTIVE_PRODUCT_STATUS' }, { title: settings.pipeline_title })}</p>
                                                            </Popover>
                                                        }
                                                    >
                                                        <InfoIcon className="cursor-pointer" />
                                                    </Whisper>
                                                </div>
                                                <div className='mt-3 d-flex align-items-center'>
                                                    <Toggle
                                                        className="col-md-5 col-lg-3 col-xl-3"
                                                        {...formik.getFieldProps('isCanAccess')}
                                                        checked={formik.values.isCanAccess!}
                                                        onChange={(e) => formik.setFieldValue('isCanAccess', e)}
                                                    />
                                                </div>
                                                {formik.touched.isCanAccess && formik.errors.isCanAccess && (
                                                    <div className='text-danger'>{formik.errors.isCanAccess}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-12 col-md-4'>
                                        <div className="d-flex flex-column p-4 gap-2" style={{ border: '1px solid #E4E6EF' }}>

                                            <label className='form-label fs-6 fw-bolder text-dark required'>
                                                {intl.formatMessage({ id: 'FORM.LABEL.COLOR' })}
                                            </label>
                                            <SketchPicker
                                                color={formik.values.color}
                                                onChange={(color) => {
                                                    formik.handleChange({
                                                        target: {
                                                            name: 'color',
                                                            value: color.hex,
                                                        },
                                                    })
                                                }}
                                                className='shadow-none border-0 p-0 w-100'
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="separator separator-dashed my-6" />
                                {/* ------------------------------------------------- CONFIG: PRICES ------------------------------------------------  */}
                                <div className='d-flex flex-column gap-2'>
                                    <div className="d-flex flex-column gap-2 mb-4">
                                        <label className='form-label fs-6 fw-bolder text-dark m-0' style={{ textTransform: 'capitalize' }}>
                                            {intl.formatMessage({ id: 'MASTER.CURRENCIES.FORM.SET_PRODUCT_PRICE_BY_CURRENCY_SIMPLE' })}
                                        </label>
                                        <span className="text-muted fs-6" style={{ textTransform: 'capitalize' }}>{intl.formatMessage({ id: "MASTER.CURRENCIES.FORM.SET_PRODUCT_PRICE_BY_CURRENCY" })}</span>
                                    </div>
                                    <div className="row mt-2 justify-content-md-end">
                                        <div className='d-flex gap-4 mb-3 col-12 col-md-5  '>
                                            <VirtualizedSelect
                                                options={_variantPriceOption}
                                                placeholder={`${intl.formatMessage({
                                                    id: 'FORM.ACTION.CHOOSE',
                                                })} ${intl.formatMessage({ id: 'PRICE_VARIANT' })}`}
                                                onChange={(option) => {
                                                    if (option) {
                                                        setSelectedVariantPrice({ id: option.value, variantPriceName: option.label, note: '' })
                                                        return
                                                    }
                                                    setSelectedVariantPrice(undefined)
                                                }}
                                                isLoading={variantPriceIsLoading}
                                                className='w-100'
                                                isClearable={true}
                                            />
                                        </div>
                                        {multiCurrencies.map((currency, index) => (
                                            <div key={currency.id} className="row mb-4">
                                                <div className="col-3">
                                                    <div className="h-100 d-flex align-items-center">
                                                        <label className='form-label fs-6 fw-semibold text-dark m-0'>
                                                            {currency.name} ({currency.code})
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className='col-9'>
                                                    <div className='d-flex gap-4 align-items-center'>
                                                        <div className="input-group">
                                                            <span className='input-group-text' style={{ minWidth: '60px', textAlign: 'center' }}>{currency.symbol}</span>

                                                            <input
                                                                value={
                                                                    Array.isArray(formik.values.prices) && formik.values.prices.length > 0 &&
                                                                    formik.values.prices.find((item) => ((Number(item.currencyId) === currency.id) && item.variantPriceId === (selectedVariantPrice?.id || null)))?.price || ''
                                                                }
                                                                onChange={(e) => {
                                                                    handlePriceChange(currency, e, selectedVariantPrice?.id ? selectedVariantPrice.id : null);
                                                                }}
                                                                className={clsx('form-control form-control-sm')}
                                                                type='text'
                                                                autoComplete='off'
                                                                style={{ zIndex: 0 }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="separator separator-dashed my-6" />

                                {
                                    checkIsHarriette ? (
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark' htmlFor="dateProduct">{intl.formatMessage({ id: "FORM.LABEL.NOTE" })}</label>
                                            <DatePicker
                                                id="dateProduct"
                                                ranges={todayRange}
                                                editable={false}
                                                format="dd MMM yyyy"
                                                value={dateProduct ? new Date(dateProduct) : null}
                                                defaultValue={null}
                                                style={{ width: "100%", zIndex: 0 }}
                                                container={document.getElementById(`update-product-modal-${product.id}`) as HTMLElement}
                                                onChange={(value: any) => {
                                                    if (value) setDateProduct(moment(value).format('YYYY-MM-DD'))
                                                    else setDateProduct(null)
                                                }}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark'>
                                                {intl.formatMessage({ id: 'FORM.LABEL.NOTE' })}
                                            </label>
                                            <div className='input-group mb-3'>
                                                <textarea
                                                    placeholder={intl.formatMessage({ id: 'FORM.LABEL.NOTE' })}
                                                    id='floatingTextarea'
                                                    {...formik.getFieldProps('note')}
                                                    className={clsx('form-control form-control-lg', {
                                                        'is-invalid': formik.touched.note && formik.errors.note,
                                                    })}
                                                    style={{ zIndex: 0 }}
                                                    name='note'
                                                    maxLength={255}
                                                    onInput={(e) => handleCaps(e)}
                                                ></textarea>
                                            </div>
                                        </>
                                    )
                                }
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light d-none"
                                    data-bs-dismiss="modal"
                                    ref={closeModalRef}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handleCloseModal}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button type="submit" className={`btn btn-primary me-10 ${modalBtnLoading ? 'disabled' : ''}`} data-kt-indicator={modalBtnLoading ? 'on' : 'off'}>
                                    <span className="indicator-label">
                                        {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}
                                    </span>
                                    <span className="indicator-progress">
                                        Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {
                showAddLabelModal && <AddLabel selectedLables={selectedLabels} setSelectedLabels={setSelectedLabels} />
            }
            {
                showAddUnitModal && <AddUnit selectedUnits={selectedUnits} setSelectedUnits={setSelectedUnits} />
            }
        </>
    )
}

export { UpdateProduct }